import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from "react";
import { Toast } from "../components/materials/Toast";
import classes from "../components/materials/Toast/classes.module.scss";
import { generateUniqueId } from "utils/UniqueIdUtils";

export enum EToastType {
	SUCCESS = "success",
	ERROR = "error",
	WARNING = "warning",
	INFO = "info",
}

export type ToastItem = {
	id: string;
	message: string;
	type: EToastType;
};

export type ToastContent = Omit<ToastItem, "id">;

interface ToastContextProps {
	toasts: ToastItem[];
	showToast: (content: ToastContent) => void;
	hideToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextProps | undefined>(undefined);

type IProps = {
	children: ReactNode;
};

const duration = 4000;

export function ToastProvider(props: IProps) {
	const [toasts, setToasts] = useState<ToastItem[]>([]);

	const showToast = useCallback((content: ToastContent) => {
		const id = generateUniqueId();
		setToasts((prevToasts) => [...prevToasts, { id, ...content }]);
	}, []);

	const hideToast = useCallback((id: string) => {
		setToasts((prev) => prev.filter((toast) => toast.id !== id));
	}, []);

	useEffect(() => {
		toasts.forEach((toast) => {
			const timer = setTimeout(() => {
				hideToast(toast.id);
			}, duration);
			return () => clearTimeout(timer);
		});
	}, [toasts, hideToast]);

	return (
		<ToastContext.Provider value={{ toasts, showToast, hideToast }}>
			{props.children}
			<div className={classes["toast-container"]}>
				{toasts.map((toast) => (
					<Toast key={toast.id} message={toast.message} type={toast.type} onClose={() => hideToast(toast.id)} />
				))}
			</div>
		</ToastContext.Provider>
	);
}

export const useToast = () => {
	const context = useContext(ToastContext);
	if (!context) {
		throw new Error("useToast must be used within a ToastProvider");
	}
	return context;
};
